import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import api from '../../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { toast } from 'sonner';
import { 
  Loader2, 
  Sparkles, 
  Bug, 
  Calendar, 
  Eye, 
  EyeOff,
  RefreshCw,
  Database
} from 'lucide-react';

const AdminChangelogPage = () => {
  const navigate = useNavigate();
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    fetchChangelog();
  }, []);

  const fetchChangelog = async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    }
    try {
      const response = await api.get('/admin/changelog');
      setVersions(response.data);
      if (isRefresh) {
        toast.success('Changelog refreshed');
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Access denied');
        navigate('/admin/dashboard');
      } else {
        toast.error('Failed to fetch changelog');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleToggleVisibility = async (entryId, currentVisible) => {
    try {
      await api.patch(`/admin/changelog/${entryId}/visibility?visible=${!currentVisible}`);
      
      // Update local state
      setVersions(prev => prev.map(version => ({
        ...version,
        changes: version.changes.map(change => 
          change.id === entryId ? { ...change, visible: !currentVisible } : change
        )
      })));
      
      toast.success(currentVisible ? 'Entry hidden from public' : 'Entry now visible to public');
    } catch (error) {
      toast.error('Failed to update visibility');
    }
  };

  const handleSeedData = async () => {
    setSeeding(true);
    try {
      const response = await api.post('/admin/changelog/seed');
      toast.success(response.data.message);
      fetchChangelog(false);
    } catch (error) {
      toast.error('Failed to seed changelog data');
    } finally {
      setSeeding(false);
    }
  };

  const handleSyncData = async () => {
    setSyncing(true);
    try {
      const response = await api.post('/admin/changelog/sync');
      toast.success(response.data.message);
      if (response.data.added > 0) {
        fetchChangelog(false);
      }
    } catch (error) {
      toast.error('Failed to sync changelog data');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        </div>
      </DashboardLayout>
    );
  }

  // Count totals
  const totalEntries = versions.reduce((acc, v) => acc + v.changes.length, 0);
  const visibleEntries = versions.reduce((acc, v) => acc + v.changes.filter(c => c.visible).length, 0);
  const hiddenEntries = totalEntries - visibleEntries;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-changelog-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-outfit text-2xl font-bold text-slate-900">Changelog Management</h1>
            <p className="text-slate-500">
              Control which changelog entries are visible to the public
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => fetchChangelog(true)} 
              disabled={refreshing}
              data-testid="refresh-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
            {versions.length === 0 ? (
              <Button 
                onClick={handleSeedData} 
                disabled={seeding}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="seed-btn"
              >
                {seeding ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Database className="h-4 w-4 mr-2" />
                )}
                Initialize Changelog
              </Button>
            ) : (
              <Button 
                onClick={handleSyncData} 
                disabled={syncing}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="sync-btn"
              >
                {syncing ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Database className="h-4 w-4 mr-2" />
                )}
                Sync Changelog
              </Button>
            )}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Total Entries</p>
              <p className="text-2xl font-bold text-slate-900">{totalEntries}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Eye className="h-4 w-4 text-emerald-600" />
                <p className="text-sm text-slate-500">Visible</p>
              </div>
              <p className="text-2xl font-bold text-emerald-600">{visibleEntries}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <EyeOff className="h-4 w-4 text-slate-400" />
                <p className="text-sm text-slate-500">Hidden</p>
              </div>
              <p className="text-2xl font-bold text-slate-400">{hiddenEntries}</p>
            </CardContent>
          </Card>
        </div>

        {/* Changelog Entries */}
        {versions.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Database className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 mb-4">No changelog entries found</p>
              <p className="text-sm text-slate-400 mb-4">
                Click "Initialize Changelog" to populate with default entries
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="font-outfit">Version History</CardTitle>
              <CardDescription>
                Toggle visibility for individual entries. Hidden entries won't appear on the public changelog.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Accordion type="multiple" className="space-y-2" defaultValue={versions.map(v => v.version)}>
                {versions.map((release) => {
                  const visibleCount = release.changes.filter(c => c.visible).length;
                  const totalCount = release.changes.length;
                  
                  return (
                    <AccordionItem 
                      key={release.version} 
                      value={release.version}
                      className="border rounded-lg px-4"
                    >
                      <AccordionTrigger className="hover:no-underline py-4">
                        <div className="flex items-center justify-between w-full pr-4">
                          <div className="flex items-center gap-3">
                            <span className="font-outfit font-semibold text-lg">
                              Version {release.version}
                            </span>
                            {release === versions[0] && (
                              <Badge className="bg-indigo-600 text-white">Latest</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-sm">
                            <span className="flex items-center gap-1 text-slate-500">
                              <Calendar className="h-4 w-4" />
                              {release.date}
                            </span>
                            <Badge variant={visibleCount === totalCount ? 'default' : visibleCount === 0 ? 'outline' : 'secondary'}>
                              {visibleCount}/{totalCount} visible
                            </Badge>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-3 pb-4">
                          {release.changes.map((change) => (
                            <div 
                              key={change.id}
                              className={`flex items-start gap-4 p-4 rounded-lg border ${
                                change.visible 
                                  ? 'bg-white border-slate-100' 
                                  : 'bg-slate-50 border-slate-200 opacity-60'
                              }`}
                              data-testid={`changelog-entry-${change.id}`}
                            >
                              <div className="flex-shrink-0 mt-0.5">
                                {change.type === 'enhancement' ? (
                                  <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                                    <Sparkles className="h-4 w-4 text-emerald-600" />
                                  </div>
                                ) : (
                                  <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                                    <Bug className="h-4 w-4 text-amber-600" />
                                  </div>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-medium text-slate-900">{change.title}</span>
                                  <Badge 
                                    variant="outline" 
                                    className={change.type === 'enhancement' 
                                      ? 'text-emerald-700 border-emerald-200 bg-emerald-50' 
                                      : 'text-amber-700 border-amber-200 bg-amber-50'
                                    }
                                  >
                                    {change.type === 'enhancement' ? 'Enhancement' : 'Bug Fix'}
                                  </Badge>
                                  {!change.visible && (
                                    <Badge variant="outline" className="text-slate-500 border-slate-300">
                                      <EyeOff className="h-3 w-3 mr-1" />
                                      Hidden
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-slate-600">{change.description}</p>
                              </div>
                              <div className="flex-shrink-0 flex items-center gap-2">
                                <span className="text-xs text-slate-500">
                                  {change.visible ? 'Visible' : 'Hidden'}
                                </span>
                                <Switch
                                  checked={change.visible}
                                  onCheckedChange={() => handleToggleVisibility(change.id, change.visible)}
                                  data-testid={`toggle-visibility-${change.id}`}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AdminChangelogPage;
